import { useState, type Dispatch } from 'react';

import type { DraftEntry, LoggerAction } from './state/loggerDraftTypes';
import { willPromptAmrap } from './amrapPromptRule';

// AMRAP confirm prompt (LG-26, measurement contract §4). A program can PRESCRIBE
// an AMRAP — that lives in `DraftSet.amrapPrescribed` and is display-only — but
// the outcome is a measurement, and a measurement is the athlete's word. The
// chip in the qualifier row is the only writer of `DraftSet.amrap`, and it
// starts OFF on a prescribed set exactly so the prescription can never become
// the measurement by default.
//
// The gap that leaves: a set the program asked for AMRAP on, completed by an
// athlete who never noticed the chip, records nothing. The test was run and the
// number is gone. So on completion of a prescribed-but-unmarked set the logger
// ASKS, once, and both answers are explicit:
//
//   yes → write the measurement (`amrap: true`, and `rir` 0 with `rpe` cleared,
//         because on an AMRAP set the effort scale collapses to a single band
//         that commits RIR 0 - measurement-contract.md, the AMRAP meter variant decided 2026-07-23. Answering yes on a
//         set where a different RIR was tapped is a correction, not a
//         contradiction: the athlete has just said the set went to the limit).
//   no / dismissed → write NOTHING. `amrap` stays false and the set is an
//         ordinary logged set. Absent is the honest reading of "not declared";
//         the one thing this prompt must never do is turn silence into a
//         measurement, which is the whole reason it exists.
//
// It fires on the completion leg only, and only for a prescribed set — the
// chip stays the way to mark an AMRAP nobody prescribed.
/** The open question, or none. Exported so the host that renders it enforces
 *  the real shape instead of falling back to `unknown`.
 *
 *  `reps` and `floor` ride along so the question can NAME them (LG-28). A bare
 *  "did you go to failure?" on a set logged at exactly the prescribed reps is a
 *  leading question: yes is the only answer that records anything, so yes is
 *  what it gets. Showing "you logged 8, the plan asked for at least 8" turns it
 *  into a question the athlete can answer wrongly on purpose but not by
 *  accident. */
export type AmrapPrompt = {
  entryIdx: number;
  setIdx: number;
  /** Reps as logged, verbatim from the cell. */
  reps: string;
  /** The prescribed floor, when the set carries one. */
  floor?: string;
} | null;

export function useAmrapConfirm(args: {
  entries: DraftEntry[];
  dispatch: Dispatch<LoggerAction>;
}) {
  const { entries, dispatch } = args;
  const [prompt, setPrompt] = useState<AmrapPrompt>(null);

  /** True when completing this set should raise the question. Exported so the
   *  other prompts on the same tap can stand down: the once-ever effort-scale
   *  intro (useEffortCommit, the 2026-07-06 incident) and the fail leg
   *  (the bodyweight marker once stood down here too, LG-27). Two modals on one gesture is a bug Antenore
   *  has already hit more than once.
   *
   *  The rule itself lives in amrapPromptRule.ts because useSetCyclePill needs
   *  the same answer one call earlier, before this hook is reachable. */
  const shouldPrompt = (entryIdx: number, setIdx: number): boolean =>
    willPromptAmrap(entries, entryIdx, setIdx);

  const maybePrompt = (entryIdx: number, setIdx: number) => {
    if (!shouldPrompt(entryIdx, setIdx)) return;
    const set = entries[entryIdx]?.sets[setIdx];
    setPrompt({
      entryIdx,
      setIdx,
      reps: set?.reps ?? '',
      ...(set?.amrapFloor ? { floor: set.amrapFloor } : {}),
    });
  };

  /** Yes — the athlete declares the set went to the limit. */
  const confirm = () => {
    if (!prompt) return;
    dispatch({
      type: 'update_set',
      entryIdx: prompt.entryIdx,
      setIdx: prompt.setIdx,
      // Same patch the chip writes, plus the RIR the collapsed band commits.
      // `completedAt` is deliberately untouched: this is an edit on an already
      // logged set, and re-stamping it would move the row in the timeline
      // (next/timeline.md, the invariant onToggleFail also holds).
      patch: { amrap: true, rir: '0', rpe: '' },
    });
    setPrompt(null);
  };

  /** No, or back, or backdrop — all the same: nothing is written. */
  const dismiss = () => setPrompt(null);

  return { prompt, shouldPrompt, maybePrompt, confirm, dismiss };
}
